import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useFormikContext } from 'formik';

import type { TAllStepsInitialValues } from '@shared/forms/placement';
import { createAllStepsInitialValues } from '@shared/forms/placement';
import { useUploadMedia } from '@features/placements/services/upload-media';

import type { TImage } from '@shared/types/image';
import { deduplicateArr } from '@shared/utils/helpers/deduplicate-arr';

export const getPlacementDataFromStorage = ():
  | TAllStepsInitialValues
  | undefined => {
  const data = localStorage.getItem('pd') || '';
  return data ? JSON.parse(data) : undefined;
};

export const setPlacementDataInStorage = (values: TAllStepsInitialValues) => {
  localStorage.setItem('pd', JSON.stringify(values));
};

export const getCurrentStepFromStorage = (): number | undefined => {
  const data = localStorage.getItem('cs') || '';
  return data ? Number(JSON.parse(data)) : undefined;
};

export const setCurrentStepInStorage = (values: number | string) => {
  localStorage.setItem('cs', JSON.stringify(values));
};

export function usePlacementsFileUploader() {
  const { setFieldValue } = useFormikContext();
  const [progressMap, setProgressMap] = useState<Record<string, number>>({});
  const { mutateAsync: uploadMediaAsync } = useUploadMedia();

  async function onDrop(
    acceptedFiles: File[],
    currentValue: (File | TImage)[] = [],
    fieldName: string
  ) {
    if (!acceptedFiles?.length) return;

    let existingFiles = [...currentValue];
    const newFiles = acceptedFiles.filter(
      (file) => !existingFiles.some((img) => img.name === file.name)
    );

    if (newFiles.length === 0) {
      return toast.error('Image already exists');
    }

    const successfullyUploaded = [];

    for (const file of acceptedFiles) {
      setFieldValue(
        fieldName,
        deduplicateArr([...acceptedFiles, ...existingFiles], 'name')
      );
      try {
        const uploadedFile = await uploadMediaAsync({
          file,
          options: {
            onUploadProgress: (progress) =>
              setProgressMap((prev) => ({
                ...prev,
                [file.name]: progress,
              })),
          },
        });

        successfullyUploaded.push(uploadedFile);

        setProgressMap((prev) => {
          const updated = { ...prev };
          delete updated[file.name];
          return updated;
        });
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }

      existingFiles = deduplicateArr(
        [...successfullyUploaded, ...existingFiles],
        'name'
      ).filter((img) => img.id);

      setFieldValue(fieldName, existingFiles);

      const [step, key] = fieldName.split('.');
      if (!step) return;
      setPlacementDataInStorage({
        ...createAllStepsInitialValues(),
        [step]: {
          ...(createAllStepsInitialValues()?.[
            step as keyof TAllStepsInitialValues
          ] ?? {}),
          [key]: existingFiles,
        },
      });
    }
  }

  return { onDrop, progressMap };
}
